import clsx from 'clsx'
import React, { use, useEffect } from 'react'

import SVGAddToCart2 from '@Assets/SVG/AddToCart2'
import SVGCheck from '@Assets/SVG/Check'
import { BlockContainer } from '@Components/BlockContainer'
import { BuyButtonV2 } from '@Components/BuyButton/v2'
import { ImageWithPins } from '@Components/Image'
import { ProductItem } from '@Domains/Product/Components/ProductItem'
import {
  Image as ImageGraphQL,
  LookDisplayBlock as LookDisplayBlockGraphQL,
  PagePresentation,
  ProductTileFragmentFragment,
} from '@Generated/graphql'
import { BlockContext } from '@Globals/CMS/Context'
import { getPins } from '@Globals/CMS/LookHelpers'
import { Image } from '@Globals/Image'
import { MetadataContext } from '@Globals/Metadata'

import styles from './LookDisplayBlock.module.scss'

function aspectRatio(image: ImageGraphQL): string {
  if (!image?.dimensions || typeof image.dimensions.width !== 'number' || typeof image.dimensions.height !== 'number') {
    return '1'
  }

  if (image.dimensions.width === 0 || image.dimensions.height === 0) {
    return '1'
  }

  if (image.dimensions.width === image.dimensions.height) {
    return '1'
  }

  return `${image.dimensions.width} / ${image.dimensions.height}`
}

const LookDisplayBlock = ({
  data,
  id,
}: {
  data: LookDisplayBlockGraphQL
  id?: string
}) => {
  const { presentation } = React.use(BlockContext)
  const { storeURL } = use(MetadataContext)
  const [addedIds, setAddedIds] = React.useState<string[]>([])

  useEffect(() => {
    const button = document.getElementById('look-go-to-cart-button')
    if (!button) {
      return
    }

    (button as HTMLButtonElement).disabled = addedIds.length === 0
  }, [addedIds])

  if (!data.asset || data.asset.__typename !== 'Image' || !data.products || data.products.length === 0) {
    return null
  }

  const pins = getPins(data, id, presentation)

  return (
    <BlockContainer
      appearance={data.appearance || {}}
      className={clsx(
        styles.block,
        presentation === PagePresentation.Sidebar ? styles.sidebar : styles.default,
      )}
      common={data.common || {}}
      id={id}
    >
      <div className={styles.content}>
        <div className={styles.asset}>
          <div className={styles.sticky} style={{ aspectRatio: aspectRatio(data.asset) }}>
            <ImageWithPins
              image={data.asset as Image}
              loading="eager"
              pins={pins}
              priority
              sizes="(max-width: 768px) 100vw, 600px"
            />
          </div>
        </div>
        <div className={styles.titleWrapper}>
          <p className={styles.title}>{data.title}</p>
        </div>
        <div className={styles.products}>
          {data.products.map((item) => {
            const product = item.product as ProductTileFragmentFragment | null | undefined
            if (!product?.id) {
              return null
            }

            return (
              <ProductItem
                key={product.id}
                action={(
                  <BuyButtonV2
                    className={clsx(styles.buyButton, addedIds.includes(product.id) && styles.success)}
                    color="black"
                    onSuccess={() => {
                      setAddedIds(prev => [...prev, product!.id])
                    }}
                    preventDefault
                    productID={product.id}
                    products={[{ quantity: 1, variantID: '', productID: product.id }]}
                    sourceID={`look_${data.id || 'unknown'}`}
                    stopPropagation
                    useCache={false}
                    withoutSuccessPrompt={presentation === PagePresentation.Sidebar}
                  >
                    {!addedIds.includes(product.id) ? <SVGAddToCart2 /> : <SVGCheck />}
                  </BuyButtonV2>
                )}
                analytics={{ source_id: 'add_to_cart_bought_together' }}
                className={styles.product}
                content={product.name}
                discount={product.masterVariant?.price?.discount?.format || ''}
                id={presentation === PagePresentation.Sidebar ? `modal_sidebar_pin_product_${product.id}` : `${id}_pin_product_${product.id}`}
                imageUrl={product.primaryAsset?.url || ''}
                price={product.masterVariant?.price?.format || ''}
                showViewProduct
                url={storeURL(product.url || '')}
              />
            )
          })}
        </div>
      </div>
    </BlockContainer>
  )
}

export default LookDisplayBlock
